/**
 * Digital Indian Market API — mock adapter (LocalStorage-backed).
 *
 * Wraps the synchronous `store.js` primitives in async functions with a small
 * artificial latency, so consumer components behave exactly the same when the
 * `http` adapter later hits a real backend.
 *
 * Every function returns a Promise resolving to the same shape the http
 * adapter will return (see /API_CONTRACT.md).
 */
import * as auth from "@/data/authRepository";
import * as business from "@/data/businessRepository";
import * as orders from "@/data/orderRepository";
import * as bookings from "@/data/bookingRepository";
import * as configuration from "@/data/configurationRepository";
import { read, write, STORAGE_KEYS as KEYS } from "@/data/storage";

const store = { ...auth, ...business, ...orders, ...bookings, ...configuration };

// Simulated network latency (ms). Set 0 in tests, 100–200 in dev to catch races.
const LATENCY_MS = 120;

function ok(data, latency = LATENCY_MS) {
  return new Promise((resolve) => setTimeout(() => resolve(data), latency));
}
function fail(message, code = 400) {
  return new Promise((_, reject) =>
    setTimeout(() => reject({ code, message }), LATENCY_MS)
  );
}

/* ---------- Auth (vendor) ---------- */
export async function loginVendor({ email, password }) {
  const v = store.getVendorByEmail(email);
  if (!v) return fail("No account found with that email.", 404);
  if (v.password !== password) return fail("Incorrect password.", 401);
  store.setSession({ email: v.email, vendorId: v.id });
  return ok({ vendor: v, sessionToken: `mock_${v.id}` });
}
export async function registerVendor({ email, password }) {
  try {
    const v = store.createVendor({ email, password });
    store.setSession({ email: v.email, vendorId: v.id });
    return ok({ vendor: v, sessionToken: `mock_${v.id}` });
  } catch (e) {
    return fail(e.message, 409);
  }
}
export async function logoutVendor() {
  store.clearSession();
  return ok({ ok: true });
}
export async function getCurrentVendor() {
  const s = store.getSession();
  if (!s?.vendorId) return ok(null);
  return ok(store.getVendorById(s.vendorId));
}

/* ---------- Password reset ---------- */
// There is no mock implementation and there will not be one: resetting a
// password means emailing a code, which needs a server. Failing loudly beats
// pretending, and `ResetPasswordPage` checks `isMockApi` before it ever calls
// these — so reaching one of them means a screen forgot to.
const RESET_UNAVAILABLE = "Password reset needs the API. Set REACT_APP_USE_MOCK_API=false.";
export async function forgotPassword() {
  return fail(RESET_UNAVAILABLE, 501);
}
export async function verifyResetOtp() {
  return fail(RESET_UNAVAILABLE, 501);
}
export async function resetPassword() {
  return fail(RESET_UNAVAILABLE, 501);
}

/* ---------- Auth (admin) ---------- */
export async function loginAdmin({ email, password }) {
  const a = store.getAdminByEmail(email);
  if (!a) return fail("No admin account found.", 404);
  if (a.password !== password) return fail("Incorrect password.", 401);
  if (a.isActive === false) return fail("This administrator account is inactive.", 403);
  store.setAdminSession({ email: a.email, adminId: a.id });
  return ok({ admin: publicAdmin(a), sessionToken: `mock_${a.id}` });
}
export async function logoutAdmin() {
  store.clearAdminSession();
  return ok({ ok: true });
}
export async function getCurrentAdmin() {
  const s = store.getAdminSession();
  if (!s?.adminId) return ok(null);
  const admin = store.getAdminById(s.adminId);
  return admin?.isActive === false ? ok(null) : ok(admin ? publicAdmin(admin) : null);
}

/* ---------- Vendor support ---------------------------------------------- */
export async function listVendorSupportTickets() {
  const session = store.getSession();
  const authorId = session?.employeeId || session?.vendorId;
  if (!authorId) return fail("Sign in to view support requests.", 401);
  return ok(read(KEYS.supportTickets, []).filter((ticket) => ticket.userId === authorId));
}

export async function createVendorSupportTicket({ subject, message }) {
  const session = store.getSession();
  const userId = session?.employeeId || session?.vendorId;
  if (!userId || !session?.vendorId) return fail("Sign in to contact support.", 401);
  const ticket = {
    id: `sup_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    restaurantId: session.vendorId,
    userId,
    subject: subject.trim(),
    message: message.trim(),
    status: "open",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  write(KEYS.supportTickets, [ticket, ...read(KEYS.supportTickets, [])]);
  return ok(ticket);
}


/* ---------- Platform admin access --------------------------------------- */
const ADMIN_PERMISSION_KEYS = [
  "manageBusinesses",
  "manageSubscriptions",
  "manageCapabilities",
  "managePlatformConfig",
  "viewPlatformAnalytics",
];

function normalisePermissions(permissions = {}) {
  return Object.fromEntries(ADMIN_PERMISSION_KEYS.map((key) => [key, permissions[key] === true]));
}

function publicAdmin(admin) {
  const { password, ...safe } = admin;
  return {
    ...safe,
    role: safe.role === "superadmin" ? "super_admin" : safe.role,
    permissions: safe.role === "super_admin" || safe.role === "superadmin"
      ? safe.permissions || {}
      : normalisePermissions(safe.permissions),
    isActive: safe.isActive !== false,
  };
}

export async function listPlatformAdmins() {
  return ok(store.getAdmins().map(publicAdmin));
}

export async function createPlatformAdmin({ email, password, name, permissions }) {
  if (store.getAdminByEmail(email)) return fail("An account with this email already exists", 409);
  const admin = {
    id: `adm_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    email: email.trim().toLowerCase(),
    password,
    name: name?.trim() || null,
    role: "admin",
    permissions: normalisePermissions(permissions),
    isActive: true,
    createdAt: new Date().toISOString(),
  };
  store.saveAdmin(admin);
  return ok(publicAdmin(admin));
}

export async function updatePlatformAdmin(id, patch) {
  const current = store.getAdminById(id);
  if (!current) return fail("Administrator not found", 404);
  if (["super_admin", "superadmin"].includes(current.role)) return fail("Super admins cannot be changed through this endpoint", 403);
  const next = {
    ...current,
    ...(patch.name !== undefined ? { name: patch.name.trim() } : {}),
    ...(patch.isActive !== undefined ? { isActive: patch.isActive } : {}),
    ...(patch.permissions !== undefined ? { permissions: normalisePermissions(patch.permissions) } : {}),
  };
  store.saveAdmin(next);
  return ok(publicAdmin(next));
}

/* ---------- Platform admin businesses ---------------------------------- */
function adminBusiness(vendor) {
  if (!vendor) return null;
  return {
    ...vendor,
    status: vendor.disabled ? "suspended" : "active",
    storefront: {
      slug: vendor.slug || null,
      logoUrl: vendor.logo || null,
      coverUrl: vendor.coverImage || null,
      tagline: vendor.tagline || null,
      description: vendor.description || null,
      address: vendor.address || null,
      phone: vendor.phone || null,
    },
    usage: {
      menuItems: vendor.items?.length || 0,
      categories: vendor.categories?.length || 0,
      orders: store.getOrders(vendor.id).length,
      customers: new Set(store.getOrders(vendor.id).map((order) => order.customer?.phone || order.customer?.email).filter(Boolean)).size,
    },
    subscription: { plan: vendor.plan || "free", status: "active" },
  };
}

export async function listAdminBusinesses({ search, status, plan, page = 1, limit = 25 } = {}) {
  let vendors = store.getVendors();
  const needle = search?.trim().toLowerCase();
  if (needle) vendors = vendors.filter((v) => [v.name, v.email, v.slug].some((value) => String(value || "").toLowerCase().includes(needle)));
  if (status && status !== "all") vendors = vendors.filter((v) => (v.disabled ? "suspended" : "active") === status);
  if (plan && plan !== "all") vendors = vendors.filter((v) => (v.plan || "free") === plan);
  const total = vendors.length;
  const start = (Math.max(1, page) - 1) * limit;
  return ok({ items: vendors.slice(start, start + limit).map(adminBusiness), meta: { page: Number(page), limit: Number(limit), total, totalPages: Math.max(1, Math.ceil(total / limit)) } });
}
export async function getAdminBusiness(id) {
  const vendor = store.getVendorById(id);
  return vendor ? ok(adminBusiness(vendor)) : fail("Business not found", 404);
}
export async function updateAdminBusinessStatus(id, status) {
  const vendor = store.getVendorById(id);
  if (!vendor) return fail("Business not found", 404);
  const next = { ...vendor, disabled: status === "suspended" };
  store.saveVendor(next);
  return ok(adminBusiness(next));
}
export async function updateAdminBusinessSubscription(id, { plan }) {
  const vendor = store.getVendorById(id);
  if (!vendor) return fail("Business not found", 404);
  const next = { ...vendor, plan };
  store.saveVendor(next);
  return ok(adminBusiness(next));
}
export async function updateAdminBusinessCapabilities(id, capabilities) {
  const vendor = store.getVendorById(id);
  if (!vendor) return fail("Business not found", 404);
  const next = { ...vendor, capabilities: { ...(vendor.capabilities || {}), ...capabilities } };
  store.saveVendor(next);
  return ok(adminBusiness(next));
}
export async function deleteAdminBusiness(id) {
  if (!store.getVendorById(id)) return fail("Business not found", 404);
  store.deleteVendor(id);
  return ok({ ok: true });
}
export async function getAdminOverview() {
  const vendors = store.getVendors();
  const allOrders = vendors.flatMap((vendor) => store.getOrders(vendor.id));
  return ok({
    businesses: { total: vendors.length, active: vendors.filter((v) => !v.disabled).length, onboarding: vendors.filter((v) => !v.onboarded).length },
    orders: { today: allOrders.filter((o) => new Date(o.createdAt).toDateString() === new Date().toDateString()).length, total: allOrders.length },
    revenue: allOrders.filter((o) => o.status !== "cancelled").reduce((sum, o) => sum + (Number(o.total) || 0), 0),
  });
}
export async function listAdminCustomerRevenue() { return ok([]); }
export async function createAdminBusiness({ name, email, phone, address, plan = "free", owner, storefront } = {}) {
  try {
    const vendor = store.createVendor({ email: owner?.email || email, password: owner?.password || "welcome1234" });
    const next = { ...vendor, name, businessName: name, phone: phone || "", address: address || "", onboarded: false, plan, slug: storefront?.slug || "" };
    store.saveVendor(next);
    return ok({ restaurant: next, owner: { email: next.email }, storefront: { slug: next.slug }, subscription: { plan, status: "active" } });
  } catch (error) { return fail(error.message, 409); }
}
export async function getAdminPersonaMatrix() { return ok(store.getPersonaMatrix()); }
export async function saveAdminPersonaMatrix(matrix) { store.savePersonaMatrix(matrix); return ok(matrix); }
export async function resetAdminPersonaMatrix() { return ok(store.resetPersonaMatrix()); }
export async function createAdminChangeRequest({ resourceType, proposedValue }) {
  if (resourceType !== "persona_matrix") return fail("Unsupported platform change request.", 422);
  const session = store.getAdminSession();
  const request = { id: `change_${Date.now()}`, resourceType, proposedValue, createdBy: session?.adminId || "mock-admin", status: "pending_review", createdAt: new Date().toISOString() };
  write(KEYS.adminChangeRequests, [request, ...read(KEYS.adminChangeRequests, [])]);
  return ok(request);
}
export async function listAdminChangeRequests(status) { return ok(read(KEYS.adminChangeRequests, []).filter((item) => !status || item.status === status)); }
export async function reviewAdminChangeRequest(id, { decision, note }) {
  const items = read(KEYS.adminChangeRequests, []);
  const current = items.find((item) => item.id === id);
  if (!current) return fail("Change request not found", 404);
  if (decision === "approved" && current.resourceType === "plan_matrix") {
    return fail("Plan matrix changes are no longer supported", 409);
  }
  const next = { ...current, status: decision, reviewNote: note || "", reviewedAt: new Date().toISOString() };
  write(KEYS.adminChangeRequests, items.map((item) => item.id === id ? next : item));
  if (decision === "approved") {
    if (current.resourceType === "persona_matrix") store.savePersonaMatrix(current.proposedValue);
  }
  return ok(next);
}
export async function listAdminOrders({ q, status, date } = {}) { const vendors = store.getVendors(); let result = vendors.flatMap((vendor) => store.getOrders(vendor.id).map((order) => ({ ...order, vendorName: vendor.name, vendorId: vendor.id }))); if (q) result = result.filter((o) => o.code.toLowerCase().includes(q.toLowerCase()) || (o.customer?.name || "").toLowerCase().includes(q.toLowerCase())); if (status && status !== "all") result = result.filter((o) => o.status === status); if (date) result = result.filter((o) => o.createdAt?.startsWith(date)); return ok(result); }
export async function listAdminSubscriptions() { return ok(store.getVendors().map((v) => ({ id: `sub_${v.id}`, restaurantId: v.id, restaurantName: v.name, restaurantEmail: v.email, plan: v.plan || "free", status: v.disabled ? "expired" : "active", startedAt: v.createdAt, expiresAt: null }))); }
export async function listAdminSupportTickets() { return ok(read(KEYS.supportTickets, [])); }
export async function updateAdminSupportTicketStatus(id, status) { const next = read(KEYS.supportTickets, []).map((ticket) => ticket.id === id ? { ...ticket, status, updatedAt: new Date().toISOString() } : ticket); write(KEYS.supportTickets, next); return ok(next.find((ticket) => ticket.id === id)); }

/* ---------- Vendors ---------- */
export async function listVendors({ q, type } = {}) {
  let vendors = store.getVendors();
  if (type && type !== "all") vendors = vendors.filter((v) => v.businessType === type);
  if (q) {
    const needle = q.toLowerCase();
    vendors = vendors.filter(
      (v) =>
        (v.name || "").toLowerCase().includes(needle) ||
        (v.tagline || "").toLowerCase().includes(needle) ||
        (v.slug || "").includes(needle)
    );
  }
  return ok(vendors);
}
export async function getVendor(id) {
  const v = store.getVendorById(id);
  return v ? ok(v) : fail("Vendor not found", 404);
}
export async function getVendorBySlug(slug) {
  const v = store.getVendorBySlug(slug);
  return v ? ok(v) : fail("Vendor not found", 404);
}
export async function updateVendor(id, patch) {
  const existing = store.getVendorById(id);
  if (!existing) return fail("Vendor not found", 404);
  const next = { ...existing, ...patch };
  store.saveVendor(next);
  return ok(next);
}
export async function deleteVendor(id) {
  store.deleteVendor(id);
  return ok({ ok: true });
}
export async function checkSlug(slug, exceptId = null) {
  return ok({ available: store.slugAvailable(slug, exceptId) });
}

/* ---------- Orders ---------- */
export async function listOrders({ vendorId, status, q } = {}) {
  let orders = store.getOrders(vendorId);
  if (status && status !== "all") orders = orders.filter((o) => o.status === status);
  if (q) {
    const needle = q.toLowerCase();
    orders = orders.filter(
      (o) =>
        o.code.toLowerCase().includes(needle) ||
        (o.customer?.name || "").toLowerCase().includes(needle)
    );
  }
  return ok(orders);
}
export async function createOrder(payload) {
  return ok(store.createOrder(payload));
}
export async function updateOrderStatus(orderId, status) {
  const o = store.updateOrderStatus(orderId, status);
  return o ? ok(o) : fail("Order not found", 404);
}

export async function getDashboardSummary({ from, to, storefrontId } = {}) {
  const vendorId = store.getSession()?.vendorId;
  const vendor = vendorId ? store.getVendorById(vendorId) : null;
  if (!vendor) return fail("Sign in to view your dashboard.", 401);

  const matchingOrders = store.getOrders(vendorId)
    .filter((order) => !storefrontId || order.storefrontId === storefrontId)
    .filter((order) => !from || order.createdAt >= from)
    .filter((order) => !to || order.createdAt <= to);
  const allOrders = matchingOrders.filter((order) => order.status !== "cancelled");
  const revenue = allOrders.reduce((sum, order) => sum + (Number(order.total) || 0), 0);
  const trendEnd = to ? new Date(to) : new Date();
  trendEnd.setHours(0, 0, 0, 0);
  const trendStart = from ? new Date(from) : new Date(trendEnd);
  if (!from) trendStart.setDate(trendStart.getDate() - 29);
  trendStart.setHours(0, 0, 0, 0);
  const trend = [];
  for (const day = new Date(trendStart); day <= trendEnd; day.setDate(day.getDate() + 1)) {
    const iso = day.toISOString().slice(0, 10);
    trend.push({
      iso,
      revenue: allOrders
        .filter((order) => (order.createdAt || "").slice(0, 10) === iso)
        .reduce((sum, order) => sum + (Number(order.total) || 0), 0),
      orders: allOrders.filter((order) => (order.createdAt || "").slice(0, 10) === iso).length,
    });
  }
  const storefronts = (vendor.storefronts || [])
    .filter((storefront) => !storefrontId || storefront.id === storefrontId)
    .map((storefront) => {
      const matching = allOrders.filter((order) => order.storefrontId === storefront.id);
      return {
        storefrontId: storefront.id,
        storefrontName: storefront.name,
        orders: matching.length,
        revenue: matching.reduce((sum, order) => sum + (Number(order.total) || 0), 0),
      };
    });

  return ok({
    kpis: { revenue, orders: allOrders.length },
    perStore: storefronts,
    trend,
    recentOrders: [...matchingOrders]
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))
      .slice(0, 6)
      .map((order) => ({
        ...order,
        code: order.code || order.orderNumber,
        items: (order.items || []).map((item) => ({ ...item, qty: item.qty ?? item.quantity })),
      })),
  });
}

/* ---------- Bookings ---------- */
export async function listBookings({ vendorId, status, q } = {}) {
  let bookings = store.getBookings(vendorId);
  if (status && status !== "all") bookings = bookings.filter((b) => b.status === status);
  if (q) {
    const needle = q.toLowerCase();
    bookings = bookings.filter(
      (b) =>
        b.code.toLowerCase().includes(needle) ||
        (b.customer?.name || "").toLowerCase().includes(needle)
    );
  }
  return ok(bookings);
}
export async function createBooking(payload) {
  return ok(store.createBooking(payload));
}
export async function updateBookingStatus(bookingId, status) {
  const b = store.updateBookingStatus(bookingId, status);
  return b ? ok(b) : fail("Booking not found", 404);
}
export async function getSlots(vendorId, dateISO) {
  return ok(store.getSlotsForDate(vendorId, dateISO));
}
